/**
 * „Porównanie tygodni" — A and B side by side, the change between them coloured toward or away from
 * the target (`model/nutritionWeeks`' `changeTone`). The Ankiety comparison's shape: a real table,
 * the change column before the two weeks, and a swap and a close in the head.
 */

import { useTranslation } from 'react-i18next';

import { ChangeArrowIcon, CloseIcon, RefusableIconButton } from '~/features/survey-report';

import type { NutritionCompareView } from '../model/nutritionWeeks';

import styles from './NutritionTab.module.css';
import survey from './SurveysTab.module.css';

const KEY = 'web:clientDetail.nutrition';

const TONE_CLASS = {
  good: styles.changeGood,
  bad: styles.changeBad,
  neutral: undefined,
} as const;

interface NutritionCompareProps {
  readonly view: NutritionCompareView;
  readonly onSwap: () => void;
  readonly onClose: () => void;
}

export function NutritionCompare({ view, onSwap, onClose }: NutritionCompareProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.paneHead}>
        <div className={styles.paneHeadText}>
          <h2 className={styles.paneTitle}>{t(`${KEY}.compareTitle`)}</h2>
          <p className={styles.paneCaption}>{t(`${KEY}.compareHint`)}</p>
        </div>
        <RefusableIconButton
          className={String(survey.streamAction)}
          label={t(`${KEY}.swap`)}
          onPress={onSwap}
          refusedReason={null}
        >
          <SwapIcon />
        </RefusableIconButton>
        <RefusableIconButton
          className={`${String(survey.streamAction)} ${String(styles.paneClose)}`}
          label={t(`${KEY}.compareClose`)}
          onPress={onClose}
          refusedReason={null}
        >
          <CloseIcon />
        </RefusableIconButton>
      </div>
      <table className={styles.weekTable}>
        <colgroup>
          <col className={styles.colCompareLabel} />
          <col className={styles.colCompareChange} />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.th} scope="col">
              {t(`${KEY}.compareQuantity`)}
            </th>
            <th className={styles.th} scope="col">
              {t(`${KEY}.compareChange`)}
            </th>
            <th className={`${String(styles.th)} ${String(styles.thWeek)}`} scope="col">
              <b className={styles.thWeekTitle}>{view.aTitle}</b>
              <span className={styles.thWeekSpan}>{view.aSpan}</span>
            </th>
            <th className={`${String(styles.th)} ${String(styles.thWeek)}`} scope="col">
              <b className={styles.thWeekTitle}>{view.bTitle}</b>
              <span className={styles.thWeekSpan}>{view.bSpan}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {view.rows.map((row) => (
            <tr key={row.key}>
              <th
                className={`${String(styles.td)} ${String(styles.compareLabel)}`}
                scope="row"
              >
                {row.label}
              </th>
              <td className={styles.td}>
                {row.change === null ? (
                  <span className={styles.dash}>—</span>
                ) : (
                  <span className={`${String(styles.change)} ${String(TONE_CLASS[row.change.tone] ?? '')}`}>
                    <ChangeArrowIcon sign={row.change.sign} />
                    {row.change.text}
                  </span>
                )}
              </td>
              <td className={`${String(styles.td)} ${String(styles.num)}`}>{row.a}</td>
              <td className={`${String(styles.td)} ${String(styles.num)}`}>{row.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** The mockup's swap arrows. */
function SwapIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.swapIcon}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />
    </svg>
  );
}
